import errorHandler from 'apis/errorHandler';
import axios, { AxiosHeaders, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { getMyInfo, getPushToken, getStoreAdmin, getToken, getXUserAgent } from 'utils/storages';
import { getAuthHost, getStoreHost } from './host';

const { CancelToken } = axios;
const source = CancelToken.source();

const authReqFulfilledInterceptor = (config: InternalAxiosRequestConfig) => {
  const pushToken = getPushToken();
  const token = getToken();
  const reConfig: InternalAxiosRequestConfig = {
    ...config,
    cancelToken: source.token,
    headers: AxiosHeaders.from(config.headers)
  };

  if (pushToken) {
    reConfig.headers.set('X-Push-Token', getXUserAgent());
  }
  if (token && config.method?.toUpperCase() === 'PUT') {
    reConfig.headers.set('Authorization', `Bearer ${token}`);
  }

  return reConfig;
};

export const authClient = axios.create({
  baseURL: getAuthHost(),
  headers: { 'X-User-Agent': getXUserAgent(), 'Content-Type': 'application/json' }
});

export const storeClient = axios.create({
  baseURL: getStoreHost(),
  headers: { 'X-User-Agent': getXUserAgent() }
});

export const authClientWithoutRequestInterceptor = axios.create({
  baseURL: getAuthHost(),
  headers: { 'X-User-Agent': getXUserAgent() }
});

// Info: authClient response, request 설정
authClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error) => {
    errorHandler && errorHandler(error);
    return Promise.reject(error);
  }
);

authClient.interceptors.request.use(authReqFulfilledInterceptor, (error) => Promise.reject(error));

// Info: storeClient response, request 설정
storeClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    return Promise.reject(error);
  }
);

storeClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const reConfig: InternalAxiosRequestConfig = {
      ...config,
      cancelToken: source.token,
      headers: AxiosHeaders.from(config.headers)
    };

    const token = getToken();
    const meInfo = getMyInfo();
    const storeAdmin = getStoreAdmin();

    if (token) reConfig.headers.set('Authorization', `Bearer ${token}`);

    if (meInfo) {
      reConfig.headers.set('X-Store-gsid', meInfo.sid);
    } else if (storeAdmin) {
      reConfig.headers.set('X-Store-gsid', storeAdmin.said);
    }

    return reConfig;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Info: authClientWithoutRequestInterceptor request 설정
authClientWithoutRequestInterceptor.interceptors.request.use(authReqFulfilledInterceptor, (error) => Promise.reject(error));

export const mockFetcher = {
  get(url: string, delay?: any) {
    const reUrl = `mock/${url}.json`;
    return new Promise<{ data: unknown }>((resolve, reject) => {
      fetch(reUrl, {
        method: 'GET'
      })
        .then((res) => res.json())
        .then((data) => {
          setTimeout(() => {
            resolve({ data });
          }, delay || 0);
        })
        .catch((e) => reject(e));
    });
  }
};

const apis = {
  authClient,
  storeClient
};

export default apis;
